/* Aurelle — global interactions. Vanilla JS, no dependencies. */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Utilities ---------- */
  window.theme = window.theme || {};

  theme.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    format = format || theme.moneyFormat || '{{amount}}';
    function fmt(number, precision, thousands, decimal) {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      var n = (number / 100).toFixed(precision);
      var parts = n.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount': return fmt(cents, 2);
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return fmt(cents, 0, ' ', ',');
        case 'amount_with_space_separator': return fmt(cents, 2, ' ', ',');
        default: return fmt(cents, 2);
      }
    });
  };

  theme.announce = function (message) {
    var el = document.getElementById('A11yAnnouncer');
    if (!el) return;
    el.textContent = '';
    window.setTimeout(function () { el.textContent = message; }, 60);
  };

  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  theme.trapFocus = function (container, first) {
    theme.releaseFocus();
    var handler = function (event) {
      if (event.key !== 'Tab') return;
      var items = Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
      if (!items.length) return;
      var firstEl = items[0], lastEl = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstEl) { event.preventDefault(); lastEl.focus(); }
      else if (!event.shiftKey && document.activeElement === lastEl) { event.preventDefault(); firstEl.focus(); }
    };
    document.addEventListener('keydown', handler);
    theme._trap = handler;
    (first || container).focus({ preventScroll: true });
  };
  theme.releaseFocus = function () {
    if (theme._trap) document.removeEventListener('keydown', theme._trap);
    theme._trap = null;
  };

  /* ---------- Modal base (drawers & search) ---------- */
  class ModalBase extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-close]')) { e.preventDefault(); this.close(); }
        else if (e.target.closest('[data-close-on-click]')) { this.close(true); }
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    get panel() { return this.querySelector('[role="dialog"]'); }
    open(opener) {
      if (this.classList.contains('is-open')) return;
      this._opener = opener || document.activeElement;
      this.hidden = false;
      document.documentElement.classList.add('is-locked');
      if (this._opener && this._opener.setAttribute) this._opener.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.classList.add('is-open');
          theme.trapFocus(this.panel, this.initialFocus && this.initialFocus());
        });
      });
    }
    close(skipFocus) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      theme.releaseFocus();
      document.documentElement.classList.remove('is-locked');
      if (this._opener && this._opener.setAttribute) this._opener.setAttribute('aria-expanded', 'false');
      var done = () => { this.hidden = true; };
      if (reducedMotion.matches) done(); else setTimeout(done, 420);
      if (!skipFocus && this._opener && this._opener.focus) this._opener.focus({ preventScroll: true });
    }
  }
  theme.ModalBase = ModalBase;

  class MenuDrawer extends ModalBase {}
  customElements.define('menu-drawer', MenuDrawer);

  class SearchModal extends ModalBase {
    initialFocus() { return this.querySelector('input[type="search"]'); }
    connectedCallback() {
      super.connectedCallback();
      var input = this.querySelector('[data-predictive]');
      var results = this.querySelector('[data-results]');
      if (!input || !results || !theme.routes.predictiveSearch) return;
      var timer;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        var q = input.value.trim();
        if (q.length < 2) { results.innerHTML = ''; return; }
        timer = setTimeout(() => {
          fetch(theme.routes.predictiveSearch + '.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6')
            .then((r) => r.json())
            .then((data) => {
              var products = (data.resources && data.resources.results && data.resources.results.products) || [];
              if (!products.length) { results.innerHTML = ''; return; }
              results.innerHTML = '<ul class="predictive-list" role="list">' + products.map(function (p) {
                var img = p.image ? '<img src="' + p.image + '&width=120" alt="" width="56" height="56" loading="lazy">' : '';
                return '<li><a class="predictive-item" href="' + p.url + '">' + img + '<span><strong>' + p.title + '</strong><br><span class="muted">' + theme.formatMoney(Math.round(parseFloat(p.price) * 100)) + '</span></span></a></li>';
              }).join('') + '</ul>';
            })
            .catch(function () { results.innerHTML = ''; });
        }, 220);
      });
    }
  }
  customElements.define('search-modal', SearchModal);

  document.addEventListener('click', function (e) {
    var menuBtn = e.target.closest('[data-menu-open]');
    if (menuBtn) { e.preventDefault(); var m = document.getElementById('MenuDrawer'); if (m) m.open(menuBtn); return; }
    var searchBtn = e.target.closest('[data-search-open]');
    if (searchBtn) { e.preventDefault(); var s = document.getElementById('SearchModal'); if (s) s.open(searchBtn); }
  });

  /* ---------- Sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.sticky !== 'true') return;
      var ticking = false;
      var update = () => {
        this.classList.toggle('is-scrolled', window.scrollY > 8);
        ticking = false;
      };
      window.addEventListener('scroll', () => {
        if (!ticking) { requestAnimationFrame(update); ticking = true; }
      }, { passive: true });
      update();
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Announcement slider (manual, no auto-rotate) ---------- */
  class AnnouncementSlider extends HTMLElement {
    connectedCallback() {
      var list = this.querySelector('.announcement-bar__list');
      this.querySelectorAll('[data-dir]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var dir = parseInt(btn.dataset.dir, 10);
          var w = list.clientWidth;
          var max = list.scrollWidth - w;
          var next = list.scrollLeft + dir * w;
          if (next > max + 2) next = 0;
          if (next < -2) next = max;
          list.scrollTo({ left: next, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        });
      });
      var delay = parseInt(this.dataset.autoplay, 10);
      if (!delay) return;
      var paused = false;
      var pause = function () { paused = true; };
      var resume = function () { paused = false; };
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);
      list.addEventListener('touchstart', pause, { passive: true });
      list.addEventListener('touchend', function () { setTimeout(resume, delay); }, { passive: true });
      this._timer = setInterval(function () {
        var w = list.clientWidth;
        var max = list.scrollWidth - w;
        if (paused || document.hidden || max < 2) return;
        var next = Math.round(list.scrollLeft / w) * w + w;
        if (next > max + 2) next = 0;
        list.scrollTo({ left: next, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }, delay);
    }
    disconnectedCallback() {
      clearInterval(this._timer);
    }
  }
  customElements.define('announcement-slider', AnnouncementSlider);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      var input = this.querySelector('input');
      this.querySelectorAll('button').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var prev = input.value;
          if (btn.name === 'plus') input.stepUp(); else input.stepDown();
          if (prev !== input.value) input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Reveal on scroll ---------- */
  function initReveal(root) {
    var items = (root || document).querySelectorAll('.reveal:not(.is-visible)');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || reducedMotion.matches || !document.body.classList.contains('has-reveal')) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach(function (el) { io.observe(el); });
  }
  theme.initReveal = initReveal;

  /* ---------- Accordion animation (progressive enhancement over <details>) ---------- */
  document.addEventListener('click', function (e) {
    var summary = e.target.closest('.accordion__summary');
    if (!summary || reducedMotion.matches || !summary.parentElement.animate) return;
    var details = summary.parentElement;
    var content = details.querySelector('.accordion__content');
    if (!content) return;
    e.preventDefault();
    if (details._anim) details._anim.cancel();
    if (details.open) {
      var start = content.offsetHeight;
      details._anim = content.animate({ height: [start + 'px', '0px'], opacity: [1, 0] }, { duration: 240, easing: 'cubic-bezier(.22,.61,.36,1)' });
      details._anim.onfinish = function () { details.open = false; details._anim = null; };
    } else {
      details.open = true;
      var end = content.offsetHeight;
      details._anim = content.animate({ height: ['0px', end + 'px'], opacity: [0, 1] }, { duration: 300, easing: 'cubic-bezier(.22,.61,.36,1)' });
      details._anim.onfinish = function () { details._anim = null; };
    }
  });

  /* ---------- Hotspots ---------- */
  class HotspotExplorer extends HTMLElement {
    connectedCallback() {
      this.pins = Array.from(this.querySelectorAll('.hotspot'));
      this.cards = Array.from(this.querySelectorAll('.hotspot-card'));
      this.panel = this.querySelector('.hotspots__panel');
      this.pins.forEach((pin) => pin.addEventListener('click', () => this.activate(parseInt(pin.dataset.index, 10), true)));
      this.cards.forEach((card) => card.addEventListener('click', () => this.activate(parseInt(card.dataset.index, 10), false)));
      if ('IntersectionObserver' in window && this.panel) {
        this.io = new IntersectionObserver((entries) => {
          if (window.innerWidth >= 990) return;
          entries.forEach((entry) => { if (entry.isIntersecting && entry.intersectionRatio > .6) this.activate(parseInt(entry.target.dataset.index, 10), false, true); });
        }, { root: this.panel, threshold: [0.6] });
        this.cards.forEach((c) => this.io.observe(c));
      }
    }
    activate(index, scroll, silent) {
      this.pins.forEach((p, i) => p.setAttribute('aria-expanded', i === index ? 'true' : 'false'));
      this.cards.forEach((c, i) => c.classList.toggle('is-active', i === index));
      var card = this.cards[index];
      if (scroll && card && window.innerWidth < 990 && this.panel) {
        this.panel.scrollTo({ left: card.offsetLeft - this.panel.offsetLeft - 20, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
      if (!silent && card) theme.announce(card.querySelector('.hotspot-card__title').textContent);
    }
  }
  customElements.define('hotspot-explorer', HotspotExplorer);

  /* ---------- Before / after ---------- */
  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      var range = this.querySelector('.ba__range');
      if (!range) return;
      var set = () => {
        this.style.setProperty('--pos', range.value + '%');
        range.setAttribute('aria-valuetext', range.value + '%');
      };
      range.addEventListener('input', set);
      set();
    }
  }
  customElements.define('before-after', BeforeAfter);

  class BaTabs extends HTMLElement {
    connectedCallback() {
      var tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;
      var select = (tab, focus) => {
        tabs.forEach((t) => {
          var on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') select(tabs[(i + 1) % tabs.length], true);
          if (e.key === 'ArrowLeft') select(tabs[(i - 1 + tabs.length) % tabs.length], true);
        });
      });
    }
  }
  customElements.define('ba-tabs', BaTabs);

  /* ---------- Carousel arrows ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-carousel-nav] [data-dir]');
    if (!btn) return;
    var track = document.getElementById(btn.closest('[data-carousel-nav]').dataset.carouselNav);
    if (!track) return;
    var item = track.firstElementChild;
    var step = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 16) : track.clientWidth * 0.8;
    track.scrollBy({ left: step * parseInt(btn.dataset.dir, 10), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  });

  /* ---------- Sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.triggers = Array.from(document.querySelectorAll('[data-sticky-trigger]'));
      this.footer = document.querySelector('.footer');
      this.visibleTriggers = new Set();
      this.footerVisible = false;
      document.body.classList.add('has-sticky-atc');

      if (!('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.footer) this.footerVisible = entry.isIntersecting;
          else if (entry.isIntersecting) this.visibleTriggers.add(entry.target);
          else this.visibleTriggers.delete(entry.target);
        });
        this.update();
      });
      this.triggers.forEach((t) => io.observe(t));
      if (this.footer) io.observe(this.footer);
      window.matchMedia('(max-width: 749px)').addEventListener('change', () => this.update());
      if (!this.triggers.length) {
        this.firstTop = window.innerHeight;
        window.addEventListener('scroll', () => this.update(), { passive: true });
      }

      if (this.dataset.action === 'scroll') {
        var form = this.querySelector('[data-sticky-form]');
        if (form) {
          form.removeAttribute('data-ajax-cart');
          form.addEventListener('submit', (e) => {
            var target = document.querySelector(this.dataset.target || '#offer');
            if (!target) return;
            e.preventDefault();
            target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
            var focusable = target.querySelector('input:checked, button[type="submit"]');
            if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), reducedMotion.matches ? 0 : 600);
          });
        }
      }

      document.addEventListener('variant:change', (e) => {
        var v = e.detail && e.detail.variant;
        if (!v) return;
        var input = this.querySelector('[data-sticky-variant]');
        if (input) input.value = v.id;
        var price = this.querySelector('[data-price-current]');
        if (price) price.textContent = theme.formatMoney(v.price);
        var compare = this.querySelector('[data-price-compare]');
        if (compare) {
          compare.hidden = !(v.compare_at_price > v.price);
          compare.textContent = v.compare_at_price > v.price ? theme.formatMoney(v.compare_at_price) : '';
        }
        var btn = this.querySelector('button[type="submit"]');
        if (btn && this.dataset.action !== 'scroll') {
          btn.disabled = !v.available;
          var label = btn.querySelector('[data-label]');
          if (label) label.textContent = v.available ? (btn.dataset.defaultLabel || label.textContent) : theme.strings.soldOut;
        }
      });
      var btn = this.querySelector('button[type="submit"] [data-label]');
      if (btn) this.querySelector('button[type="submit"]').dataset.defaultLabel = btn.textContent;
    }
    update() {
      var show;
      if (this.triggers.length) {
        var anyVisible = this.visibleTriggers.size > 0;
        var anyAbove = this.triggers.some(function (t) { return t.getBoundingClientRect().bottom < 0; });
        var mobile = window.matchMedia('(max-width: 749px)').matches;
        show = !anyVisible && (anyAbove || mobile) && !this.footerVisible;
      } else {
        show = window.scrollY > this.firstTop && !this.footerVisible;
      }
      if (show === this._shown) return;
      this._shown = show;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (show) this.removeAttribute('inert'); else this.setAttribute('inert', '');
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Offer section: bundles ---------- */
  class ProductOffer extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.qtyInput = this.querySelector('[data-quantity-input]');
      this.variantInput = this.querySelector('[data-variant-input]');
      this.addEventListener('change', (e) => {
        if (e.target.classList.contains('bundle__input')) this.applyBundle(e.target);
        if (e.target === this.variantInput && e.target.tagName === 'SELECT') this.updateBundlePrices();
      });
      var checked = this.querySelector('.bundle__input:checked');
      if (checked) this.applyBundle(checked);
    }
    applyBundle(radio) {
      if (this.qtyInput) this.qtyInput.value = radio.dataset.quantity || 1;
      if (radio.dataset.extraId) this.form.dataset.extraId = radio.dataset.extraId;
      else delete this.form.dataset.extraId;
    }
    updateBundlePrices() {
      var opt = this.variantInput.options[this.variantInput.selectedIndex];
      var price = parseInt(opt.dataset.price, 10);
      var compare = parseInt(opt.dataset.compare, 10) || price;
      this.querySelectorAll('.bundle__input').forEach((radio) => {
        var q = parseInt(radio.dataset.quantity, 10) || 1;
        var d = parseInt(radio.dataset.discount, 10) || 0;
        var base = radio.dataset.fromCompare ? compare : price;
        var p = (base - (radio.dataset.fromCompare ? Math.round(base * d / 100) : Math.floor(base * d / 100))) * q, c = compare * q;
        if (radio.dataset.extraPrice) { p += parseInt(radio.dataset.extraPrice, 10); c += parseInt(radio.dataset.extraCompare, 10); }
        var label = radio.closest('.bundle');
        var cur = label.querySelector('[data-bundle-price]');
        var cmp = label.querySelector('[data-bundle-compare]');
        if (cur) cur.textContent = theme.formatMoney(p);
        if (cmp) { cmp.textContent = theme.formatMoney(c); cmp.hidden = !(c > p); }
        var save = label.querySelector('.bundle__save');
        if (save) {
          save.hidden = !(c > p);
          save.textContent = (this.dataset.saveAmount || '[amount]').replace('[amount]', theme.formatMoney(c - p).replace(/[.,]00(?!\d)/, ''));
        }
      });
    }
  }
  customElements.define('product-offer', ProductOffer);

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('.product-grid') || !this.dataset.url) return;
      var load = () => {
        fetch(this.dataset.url)
          .then((r) => r.text())
          .then((text) => {
            var html = new DOMParser().parseFromString(text, 'text/html');
            var fresh = html.querySelector('product-recommendations');
            if (fresh && fresh.innerHTML.trim().length) { this.innerHTML = fresh.innerHTML; initReveal(this); }
            else this.remove();
          })
          .catch(() => {});
      };
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) { io.disconnect(); load(); }
        }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      } else load();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Boot ---------- */
  document.addEventListener('DOMContentLoaded', function () { initReveal(); });
  if (document.readyState !== 'loading') initReveal();

  /* Theme editor support */
  document.addEventListener('shopify:section:load', function (e) { initReveal(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var card = e.target.closest ? e.target.closest('.hotspot, .hotspot-card') : null;
    var explorer = e.target.closest ? e.target.closest('hotspot-explorer') : null;
    if (card && explorer) explorer.activate(parseInt(card.dataset.index, 10), true, true);
    var panel = e.target.closest ? e.target.closest('.before-after__panel') : null;
    if (panel && panel.id) {
      var tab = document.querySelector('[aria-controls="' + panel.id + '"]');
      if (tab) tab.click();
    }
  });
})();
